import { createAdminClient, type AdminClient, type AdminCredentials } from '../modules/core/index.js'
import { createClientsAdminApi, type ClientsAdminApi } from '../modules/clients/index.js'
import { createAccountsAdminApi, type AccountsAdminApi } from '../modules/accounts/index.js'
import { createAuditAdminApi, type AuditAdminApi } from '../modules/audit/index.js'
import { createFacilitiesAdminApi, type FacilitiesAdminApi } from '../modules/facilities/index.js'
import { createRosterAdminApi, type RosterAdminApi } from '../modules/roster/index.js'
import { createBehaviorsAdminApi, type BehaviorsAdminApi } from '../modules/behaviors/index.js'
import { createPartnerAdminApi, type PartnerAdminApi } from '../modules/partner/index.js'
import { createScoringAdminApi, type ScoringAdminApi } from '../modules/scoring/index.js'
import { createScheduleAdminApi, type ScheduleAdminApi } from '../modules/schedule/index.js'
import { createMessagesAdminApi, type MessagesAdminApi } from '../modules/messages/index.js'
import { createFeedbackAdminApi, type FeedbackAdminApi } from '../modules/feedback/index.js'
import { createAddOnsAdminApi, type AddOnsAdminApi } from '../modules/add-ons/index.js'
import { createSystemAdminApi, type SystemAdminApi } from '../modules/system/index.js'

/**
 * Everything a signed-in SeeWhoCares admin can ask the API: each module's
 * admin calls (`modules/<name>/adminApi.ts`) composed over one
 * credential-bound client, the web's counterpart of the API's container
 * (web modules plan phase 3). The admin shell builds one at sign-in and
 * hands it down; each screen declares the parts it uses, and this satisfies
 * any of them. An add-on's admin sections get `client` and build their own
 * calls, as the add-on mounts its own routes on the API.
 */
export type AdminApi = { readonly client: AdminClient } & ClientsAdminApi &
  AccountsAdminApi &
  AuditAdminApi &
  FacilitiesAdminApi &
  RosterAdminApi &
  BehaviorsAdminApi &
  PartnerAdminApi &
  ScoringAdminApi &
  ScheduleAdminApi &
  MessagesAdminApi &
  FeedbackAdminApi &
  AddOnsAdminApi &
  SystemAdminApi

export function createAdminApi(baseUrl: string, credentials: AdminCredentials): AdminApi {
  const client = createAdminClient(baseUrl, credentials)
  return {
    client,
    ...createClientsAdminApi(client),
    ...createAccountsAdminApi(client),
    ...createAuditAdminApi(client),
    ...createFacilitiesAdminApi(client),
    ...createRosterAdminApi(client),
    ...createBehaviorsAdminApi(client),
    ...createPartnerAdminApi(client),
    ...createScoringAdminApi(client),
    ...createScheduleAdminApi(client),
    ...createMessagesAdminApi(client),
    ...createFeedbackAdminApi(client),
    ...createAddOnsAdminApi(client),
    ...createSystemAdminApi(client),
  }
}
